import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { categoryService } from '../../services/categoryService';
import { userService } from '../../services/userService';
import { Course, CourseCategory, User } from '../../types';
import { getMediaUrl } from '../../utils/media';

interface CourseFormData {
  title: string;
  description: string;
  thumbnail: string;
  price: number;
  category_id?: number;
  level?: Course['level'];
  status: Course['status'];
  video_type?: Course['video_type'];
  experience_points?: number;
}

interface CourseFormState extends CourseFormData {
  thumbnailFile?: File | null;
  thumbnailPreview?: string | null;
}

const initialCourseForm: CourseFormData = {
  title: '',
  description: '',
  thumbnail: '',
  price: 0,
  status: 'draft',
  level: 'beginner',
  video_type: 'long_video',
  experience_points: 0,
};

const AdminCoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [instructors, setInstructors] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | Course['status']>('all');

  const [creating, setCreating] = useState(false);
  const [newCourse, setNewCourse] = useState<CourseFormState>({ ...initialCourseForm, thumbnailFile: null, thumbnailPreview: null });
  const [newCourseInstructor, setNewCourseInstructor] = useState<number | 'self'>('self');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingCourse, setEditingCourse] = useState<CourseFormState>({ ...initialCourseForm, thumbnailFile: null, thumbnailPreview: null });
  const [editingInstructor, setEditingInstructor] = useState<number | 'self'>('self');
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesResult, categoriesResult, usersResult] = await Promise.allSettled([
        courseService.getCourses(1, 100, undefined, statusFilter === 'all' ? 'all' : statusFilter),
        categoryService.list(),
        userService.listUsers({ per_page: 200, role: 'instructor' }),
      ]);

      if (coursesResult.status === 'fulfilled') {
        setCourses(coursesResult.value.courses);
      } else {
        console.error('Error loading courses:', coursesResult.reason);
      }

      if (categoriesResult.status === 'fulfilled') {
        setCategories(categoriesResult.value);
      } else {
        console.error('Error loading categories:', categoriesResult.reason);
      }

      if (usersResult.status === 'fulfilled') {
        setInstructors(usersResult.value.users);
      } else {
        console.error('Error loading instructors:', usersResult.reason);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filteredCourses = useMemo(() => {
    if (!query.trim()) return courses;
    const lower = query.toLowerCase();
    return courses.filter(
      (course) =>
        course.title.toLowerCase().includes(lower) ||
        course.description?.toLowerCase().includes(lower) ||
        course.category?.name?.toLowerCase().includes(lower)
    );
  }, [courses, query]);

  const handleNewThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewCourse((prev) => ({ ...prev, thumbnailFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewCourse((prev) => ({ ...prev, thumbnailPreview: reader.result as string }));
      };
      reader.readAsDataURL(file);
    } else {
      setNewCourse((prev) => ({ ...prev, thumbnailPreview: null }));
    }
  };

  const handleEditThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setEditingCourse((prev) => ({ ...prev, thumbnailFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingCourse((prev) => ({ ...prev, thumbnailPreview: reader.result as string }));
      };
      reader.readAsDataURL(file);
    } else {
      setEditingCourse((prev) => ({ ...prev, thumbnailPreview: null }));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCourse.title.trim()) {
      alert('Tiêu đề không được trống');
      return;
    }
    setCreating(true);
    try {
      const payload: any = {
        title: newCourse.title,
        description: newCourse.description,
        price: Number(newCourse.price) || 0,
        status: newCourse.status,
        level: newCourse.level,
        video_type: newCourse.video_type || 'long_video',
        thumbnailFile: newCourse.thumbnailFile || undefined,
      };
      if (!newCourse.description) delete payload.description;
      if (newCourse.category_id) payload.category_id = newCourse.category_id;
      if (!newCourse.thumbnailFile && newCourse.thumbnail) {
        payload.thumbnail = newCourse.thumbnail;
      }

      // Optionally allow selecting instructor user (if not self)
      if (newCourseInstructor !== 'self') {
        payload.instructor_id = newCourseInstructor;
      }

      const { course } = await courseService.createCourse(payload);
      setCourses((prev) => [course, ...prev]);
      setNewCourse({ ...initialCourseForm, thumbnailFile: null, thumbnailPreview: null });
      setNewCourseInstructor('self');
      alert('Tạo khóa học thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể tạo khóa học');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (course: Course) => {
    setEditingId(course.id);
    setEditingCourse({
      title: course.title,
      description: course.description || '',
      thumbnail: course.thumbnail || '',
      price: course.price,
      category_id: course.category_id,
      level: course.level,
      status: course.status,
      video_type: course.video_type || 'long_video',
      experience_points: course.experience_points || 0,
      thumbnailFile: null,
      thumbnailPreview: course.thumbnail ? getMediaUrl(course.thumbnail) : null,
    });
    setEditingInstructor('self');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingCourse({ ...initialCourseForm, thumbnailFile: null, thumbnailPreview: null });
    setEditingInstructor('self');
  };

  const saveEdit = async (courseId: number) => {
    if (!editingCourse.title.trim()) {
      alert('Tiêu đề không được trống');
      return;
    }
    setSavingEdit(true);
    try {
      const payload: any = {
        title: editingCourse.title,
        description: editingCourse.description,
        price: Number(editingCourse.price) || 0,
        status: editingCourse.status,
        level: editingCourse.level,
        video_type: editingCourse.video_type || 'long_video',
        thumbnailFile: editingCourse.thumbnailFile || undefined,
      };
      if (!editingCourse.description) payload.description = null;
      if (editingCourse.category_id) {
        payload.category_id = editingCourse.category_id;
      } else {
        payload.category_id = null;
      }
      if (!editingCourse.thumbnailFile && editingCourse.thumbnail) {
        payload.thumbnail = editingCourse.thumbnail;
      }

      if (editingInstructor !== 'self') {
        payload.instructor_id = editingInstructor;
      }

      const { course } = await courseService.updateCourse(courseId, payload);
      setCourses((prev) => prev.map((c) => (c.id === courseId ? course : c)));
      cancelEdit();
      alert('Cập nhật khóa học thành công');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể cập nhật khóa học');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteCourse = async (courseId: number) => {
    if (!window.confirm('Xóa khóa học này? (Video và ghi danh sẽ bị xóa theo)')) return;
    try {
      await courseService.deleteCourse(courseId);
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể xóa khóa học');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Thêm khóa học mới</h2>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreate}>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Tiêu đề*</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.title}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Mô tả</span>
            <textarea
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              value={newCourse.description}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-gray-700">Ảnh đại diện (Thumbnail)</span>
            <input
              type="file"
              accept="image/*"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              onChange={handleNewThumbnailChange}
            />
            {newCourse.thumbnailPreview && (
              <div className="mt-2">
                <img
                  src={newCourse.thumbnailPreview}
                  alt="Thumbnail preview"
                  className="max-w-xs max-h-48 object-contain border border-gray-300 rounded-lg"
                />
              </div>
            )}
            {!newCourse.thumbnailFile && (
              <input
                type="text"
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Hoặc nhập URL ảnh"
                value={newCourse.thumbnail}
                onChange={(e) => setNewCourse((prev) => ({ ...prev, thumbnail: e.target.value }))}
              />
            )}
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Giá (VNĐ)</span>
            <input
              type="number"
              min={0}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.price}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, price: Number(e.target.value) }))}
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Danh mục</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.category_id ?? ''}
              onChange={(e) =>
                setNewCourse((prev) => ({ ...prev, category_id: e.target.value ? Number(e.target.value) : undefined }))
              }
            >
              <option value="">-- Không chọn --</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Trình độ</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.level || ''}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, level: e.target.value as Course['level'] }))}
            >
              <option value="beginner">Cơ bản</option>
              <option value="intermediate">Trung bình</option>
              <option value="advanced">Nâng cao</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Trạng thái</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.status}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, status: e.target.value as Course['status'] }))}
            >
              <option value="draft">Nháp</option>
              <option value="published">Đã xuất bản</option>
              <option value="archived">Lưu trữ</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Loại video</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourse.video_type || 'long_video'}
              onChange={(e) => setNewCourse((prev) => ({ ...prev, video_type: e.target.value as Course['video_type'] }))}
            >
              <option value="long_video">Video dài</option>
              <option value="short_video">Video ngắn</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Giảng viên</span>
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newCourseInstructor}
              onChange={(e) =>
                setNewCourseInstructor(e.target.value === 'self' ? 'self' : Number(e.target.value))
              }
            >
              <option value="self">Chính tôi (tài khoản hiện tại)</option>
              {instructors.map((inst) => (
                <option key={inst.id} value={inst.id}>
                  #{inst.id} - {inst.full_name || inst.username}
                </option>
              ))}
            </select>
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Đang tạo...' : 'Tạo khóa học'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <h2 className="text-lg font-medium">Danh sách khóa học</h2>
          <div className="flex gap-2 flex-wrap">
            <input
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Tìm theo tên, mô tả..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="draft">Nháp</option>
              <option value="published">Đã xuất bản</option>
              <option value="archived">Lưu trữ</option>
            </select>
            <button
              onClick={fetchData}
              className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm"
              disabled={loading}
            >
              {loading ? 'Đang tải...' : 'Làm mới'}
            </button>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Tiêu đề</th>
                <th className="py-2 pr-4">Danh mục</th>
                <th className="py-2 pr-4">Giá</th>
                <th className="py-2 pr-4">⭐ Điểm XP</th>
                <th className="py-2 pr-4">Trình độ</th>
                <th className="py-2 pr-4">Loại video</th>
                <th className="py-2 pr-4">Trạng thái</th>
                <th className="py-2 pr-4">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map((course) => {
                const isEditing = editingId === course.id;
                return (
                  <React.Fragment key={course.id}>
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">{course.id}</td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingCourse.title}
                            onChange={(e) => setEditingCourse((prev) => ({ ...prev, title: e.target.value }))}
                          />
                        ) : (
                          course.title
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingCourse.category_id ?? ''}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({
                                ...prev,
                                category_id: e.target.value ? Number(e.target.value) : undefined,
                              }))
                            }
                          >
                            <option value="">-- Không chọn --</option>
                            {categories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          course.category?.name || '-'
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingCourse.price}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({ ...prev, price: Number(e.target.value) || 0 }))
                            }
                          />
                        ) : (
                          `${course.price.toLocaleString('vi-VN')} đ`
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            className="border-2 border-red-300 rounded-xl px-3 py-2 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none w-full"
                            value={editingCourse.experience_points || 0}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({ ...prev, experience_points: Number(e.target.value) || 0 }))
                            }
                          />
                        ) : (
                          <span className="font-semibold text-red-600">{course.experience_points || 0} XP</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingCourse.level || ''}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({ ...prev, level: e.target.value as Course['level'] }))
                            }
                          >
                            <option value="beginner">Cơ bản</option>
                            <option value="intermediate">Trung bình</option>
                            <option value="advanced">Nâng cao</option>
                          </select>
                        ) : (
                          course.level
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingCourse.video_type || 'long_video'}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({ ...prev, video_type: e.target.value as Course['video_type'] }))
                            }
                          >
                            <option value="long_video">Video dài</option>
                            <option value="short_video">Video ngắn</option>
                          </select>
                        ) : (
                          course.video_type === 'short_video' ? 'Video ngắn' : 'Video dài'
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingCourse.status}
                            onChange={(e) =>
                              setEditingCourse((prev) => ({ ...prev, status: e.target.value as Course['status'] }))
                            }
                          >
                            <option value="draft">Nháp</option>
                            <option value="published">Đã xuất bản</option>
                            <option value="archived">Lưu trữ</option>
                          </select>
                        ) : (
                          course.status
                        )}
                      </td>
                      <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(course.id)}
                              className="px-3 py-1 rounded-lg bg-green-600 text-white"
                              disabled={savingEdit}
                            >
                              {savingEdit ? 'Đang lưu...' : 'Lưu'}
                            </button>
                            <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700">
                              Hủy
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => navigate(`/admin/courses/${course.id}`)}
                              className="px-3 py-1 rounded-lg bg-primary-600 text-white"
                            >
                              Quản lý chi tiết
                            </button>
                            <button
                              onClick={() => startEdit(course)}
                              className="px-3 py-1 rounded-lg bg-blue-600 text-white"
                            >
                              Sửa
                            </button>
                            <button
                              onClick={() => deleteCourse(course.id)}
                              className="px-3 py-1 rounded-lg bg-red-600 text-white"
                            >
                              Xóa
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr>
                        <td colSpan={7} className="py-4 px-4 bg-gray-50">
                          <div className="space-y-2">
                            <label className="block text-sm">
                              <span className="text-gray-700 font-medium">Ảnh đại diện (Thumbnail)</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                                onChange={handleEditThumbnailChange}
                              />
                              {editingCourse.thumbnailPreview && (
                                <div className="mt-2">
                                  <img
                                    src={editingCourse.thumbnailPreview || ''}
                                    alt="Thumbnail preview"
                                    className="max-w-xs max-h-48 object-contain border border-gray-300 rounded-lg"
                                  />
                                </div>
                              )}
                              {!editingCourse.thumbnailFile && (
                                <input
                                  type="text"
                                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                                  placeholder="Hoặc nhập URL ảnh"
                                  value={editingCourse.thumbnail}
                                  onChange={(e) => setEditingCourse((prev) => ({ ...prev, thumbnail: e.target.value }))}
                                />
                              )}
                            </label>
                            <label className="block text-sm">
                              <span className="text-gray-700 font-medium">Mô tả</span>
                              <textarea
                                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                                rows={3}
                                value={editingCourse.description}
                                onChange={(e) => setEditingCourse((prev) => ({ ...prev, description: e.target.value }))}
                              />
                            </label>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCoursesPage;



